// src/features/incidents/ImageAnalysisMainPage.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Stack,
  Alert,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton
} from '@mui/material';
import {
  Search,
  ImageSearch,
  Visibility,
  Warning,
  PhotoLibrary
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../i18n/i18n';
import { getIncidents } from './incidentsService';

export default function ImageAnalysisMainPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [incidents, setIncidents] = useState([]);
  const [filteredIncidents, setFilteredIncidents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadIncidents();
  }, []);

  useEffect(() => {
    filterIncidents();
  }, [searchTerm, incidents]);

  const loadIncidents = async () => {
    try {
      setLoading(true);
      const allIncidents = await getIncidents();
      // Фільтруємо інциденти з фото
      const incidentsWithPhotos = allIncidents.filter(incident => 
        incident.photos && incident.photos.length > 0
      );
      setIncidents(incidentsWithPhotos);
    } catch (error) {
      console.error('Error loading incidents:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterIncidents = () => {
    if (!searchTerm) {
      setFilteredIncidents(incidents);
      return;
    }

    const filtered = incidents.filter(incident =>
      incident.clientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      incident.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      incident.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredIncidents(filtered);
  };

  const handleAnalyzeIncident = (incidentId) => {
    navigate(`/incidents/${incidentId}/image-analysis`);
  };

  const getIncidentStatusColor = (status) => {
    const colors = {
      open: 'warning',
      under_review: 'info',
      in_progress: 'primary',
      resolved: 'success',
      closed: 'default'
    };
    return colors[status] || 'default';
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6">
          {t('common_loading') || 'Loading...'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Заголовок */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            🔍 {t('image_analysis_title') || 'AI Image Analysis'}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {t('image_analysis_main_subtitle') || 'Select an incident with photos to analyze'}
          </Typography>
        </Box>
      </Stack>

      {/* Статистика */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <PhotoLibrary color="primary" />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    {t('image_analysis_incidents_with_photos') || 'Incidents with Photos'}
                  </Typography>
                  <Typography variant="h4">
                    {incidents.length}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <ImageSearch color="secondary" />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    {t('image_analysis_total_photos') || 'Total Photos'}
                  </Typography>
                  <Typography variant="h4">
                    {incidents.reduce((total, incident) => total + (incident.photos?.length || 0), 0)}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Warning color="warning" />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    {t('image_analysis_ready_for_analysis') || 'Ready for Analysis'}
                  </Typography>
                  <Typography variant="h4">
                    {incidents.length}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Пошук */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder={t('image_analysis_search_placeholder') || 'Search incidents by client, type or description...'}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Список інцидентів */}
      {filteredIncidents.length === 0 ? (
        <Alert severity="info">
          {incidents.length === 0 
            ? (t('image_analysis_no_incidents_with_photos') || 'No incidents with photos found.')
            : (t('image_analysis_no_matching_incidents') || 'No incidents match your search criteria.')
          }
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('common_date') || 'Date'}</TableCell>
                <TableCell>{t('incidents_client') || 'Client'}</TableCell>
                <TableCell>{t('incidents_type') || 'Type'}</TableCell>
                <TableCell>{t('common_status') || 'Status'}</TableCell>
                <TableCell>{t('incidents_photos') || 'Photos'}</TableCell>
                <TableCell align="right">{t('actions_actions') || 'Actions'}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredIncidents.map((incident) => (
                <TableRow key={incident.id} hover>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(incident.date).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight="medium">
                      {incident.clientName || incident.clientId}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={incident.type} 
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={incident.status} 
                      size="small"
                      color={getIncidentStatusColor(incident.status)}
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <PhotoLibrary fontSize="small" color="action" />
                      <Typography>
                        {incident.photos?.length || 0}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <IconButton 
                        size="small"
                        onClick={() => navigate(`/incidents/${incident.id}`)}
                        title={t('actions_view') || 'View Incident'}
                      >
                        <Visibility />
                      </IconButton>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<ImageSearch />}
                        onClick={() => handleAnalyzeIncident(incident.id)}
                      >
                        {t('image_analysis_analyze') || 'Analyze'}
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}